"use client"

import type React from "react"
import { useRef, useEffect, useState } from "react"
import { ChevronRight } from "lucide-react"
import { motion, useScroll, useTransform, useSpring, useMotionValue, animate } from "framer-motion"
import Link from 'next/link';

// Component hiển thị hình ảnh chuyển động theo scroll
const FloatingImage = ({
    src,
    style,
    delay,
    speed = 1,
    maxOpacity = 1,
    className,
}: {
    src: string;
    style: any;
    delay: number;
    speed?: number;
    maxOpacity?: number;
    className?: string;
}) => {
    const ref = useRef(null)

    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    })

    const smoothProgress = useSpring(scrollYProgress, {
        stiffness: 100,
        damping: 30,
        restDelta: 0.001,
    })

    const opacity = useTransform(smoothProgress, [0, 0.3, 0.7, 1], [0, maxOpacity, maxOpacity, 0])
    const scale = useTransform(smoothProgress, [0, 0.5, 1], [0.5, 1, 0.5])
    const y = useTransform(smoothProgress, [0, 1], [100 * speed * 0.3, -100 * speed * 0.3])

    return (
        <motion.div
            ref={ref}
            style={{
                ...style,
                opacity,
                scale,
                y,
            }}
            className={`absolute rounded-2xl overflow-hidden shadow-xl will-change-transform ${className || "w-10 h-10 md:w-14 md:h-14 xl:w-22 xl:h-22 2xl:w-28 2xl:h-28 min-[1920px]:w-52 min-[1920px]:h-52 min-[2560px]:w-80 min-[2560px]:h-80"}`}
            animate={{
                y: [0, -10, 0],
            }}
            transition={{
                duration: 4 + Math.random() * 2,
                repeat: Number.POSITIVE_INFINITY,
                ease: "easeInOut",
                delay: delay,
            }}
        >
            <img
                src={src || "/placeholder.svg"}
                alt="Floating element"
                className="w-full h-full object-cover"
            />
        </motion.div>
    )
}

// Component hiển thị số đếm động
const Counter = ({ value, className, style }: { value: number; className?: string; style?: React.CSSProperties }) => {
    const count = useMotionValue(0)
    const rounded = useTransform(count, (latest) => Math.round(latest).toString().padStart(2, "0"))
    const [displayValue, setDisplayValue] = useState("00")

    useEffect(() => {
        const controls = animate(count, value, {
            duration: 3,
            ease: "easeOut",
        })
        return controls.stop
    }, [count, value])

    useEffect(() => {
        return rounded.on("change", (v) => setDisplayValue(v))
    }, [rounded])

    return (
        <motion.div className={className} style={style}>
            {displayValue}
        </motion.div>
    )
}

// Component Hero section của trang Rovi Holdings
const RoviHoldingsHero: React.FC = () => {
    // Danh sách hình ảnh hiển thị cho desktop
    const desktopImages = [
        { src: "https://nipgroup.com/wp-content/uploads/Construction_contractor_hard_hat-1200x675.png", top: "13%", left: "13%", speed: 1.2 },
        { src: "https://media.gettyimages.com/id/1390286175/vector/antique-illustration-of-usa-new-york-landmarks-and-companies-buffalo-niagara-hotel.jpg?s=612x612&w=gi&k=20&c=ZwiLHaeTml8ZqT5ruCWefIr7Ovc7mAC3kW1zGAjNFOw=", top: "30%", right: "48%", speed: 1.5 },
        { src: "https://images.unsplash.com/photo-1515879218367-8466d910aaa4?fm=jpg&q=60&w=3000&ixlib=rb-4.1.0", top: "12%", right: "8%", speed: 0.8 },
        { src: "https://media.istockphoto.com/id/1383533798/photo/shot-of-a-group-of-businesspeople-clapping-hands-during-a-conference.jpg?s=612x612&w=0&k=20&c=IfDGnPBbV4ELNgOHJdnWdlGuls54SN9CkVeOpxn3W4s=", bottom: "22%", left: "65%", speed: 1.1 },
        { src: "https://t4.ftcdn.net/jpg/06/46/48/39/360_F_646483996_FU8STGnemtNlh7eprlfh1fZtBmAW8lV2.jpg", top: "72%", right: "6%", speed: 0.9, maxOpacity: 0.3 },
        { src: "https://as1.ftcdn.net/jpg/03/05/50/86/1000_F_305508687_6aqst6Jq9KXHl9bm3mShJFqX48M3LSoI.jpg", bottom: "18%", left: "40%", speed: 0.3 },
        { src: "https://www.shutterstock.com/image-photo/motion-blurred-car-driving-on-600nw-2446284117.jpg", top: "21%", right: "33%", speed: 2, maxOpacity: 0.5 },
        { src: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8YnVzaW5lc3MlMjBidWlsZGluZ3xlbnwwfHwwfHx8MA%3D%3D", top: "50%", right: "13%", speed: 0.7, maxOpacity: 0.3 },
        { src: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8dGVhbXdvcmt8ZW58MHx8MHx8fDA%3D", bottom: "50%", right: "0%", speed: 1.6, maxOpacity: 0.5 },
    ]

    // Danh sách hình ảnh hiển thị cho mobile
    const mobileImages = [
        { src: desktopImages[0].src, top: "60%", left: "5%", speed: 1.2 },
        { src: desktopImages[2].src, top: "75%", right: "5%", speed: 0.8 },
        { src: desktopImages[3].src, top: "55%", right: "15%", speed: 1.1 },
        { src: desktopImages[5].src, bottom: "10%", left: "10%", speed: 0.9 },
        { src: desktopImages[6].src, top: "85%", left: "40%", speed: 1.5 },
    ]

    return (
        // Section hiển thị hero banner
        <section className="relative w-full min-h-[500px] md:min-h-0 md:aspect-[16/9] flex flex-col justify-center bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">

            {/* Danh sách hình ảnh cho mobile */}
            <div className="md:hidden absolute inset-0 z-0 pointer-events-none">
                {mobileImages.map((img, idx) => (
                    <FloatingImage
                        key={`mobile-${idx}`}
                        src={img.src}
                        delay={idx * 0.5}
                        speed={img.speed}
                        maxOpacity={0.8}
                        className="w-12 h-12"
                        style={{
                            top: img.top ?? "auto",
                            bottom: img.bottom ?? "auto",
                            left: img.left ?? "auto",
                            right: img.right ?? "auto",
                        }}
                    />
                ))}
            </div>

            {/* Danh sách hình ảnh cho desktop */}
            <div className="hidden md:block absolute inset-0 z-0 pointer-events-none">
                {desktopImages.map((img, idx) => (
                    <FloatingImage
                        key={`desktop-${idx}`}
                        src={img.src}
                        delay={idx * 0.5}
                        speed={img.speed || 1}
                        maxOpacity={img.maxOpacity}
                        style={{
                            top: img.top ?? "auto",
                            bottom: img.bottom ?? "auto",
                            left: img.left ?? "auto",
                            right: img.right ?? "auto",
                        }}
                    />
                ))}
            </div>

            {/* Khối nội dung chính */}
            <div className="relative z-10 container mx-auto px-6 pt-8 pb-12 md:py-32 xl:py-40 2xl:py-52 min-[1920px]:py-60 min-[2560px]:py-96 md:pl-24 xl:pl-32 2xl:pl-40 min-[1920px]:pl-52 min-[2560px]:pl-80 min-[1920px]:max-w-[95%] min-[2560px]:max-w-full">

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 min-[1920px]:gap-32 min-[2560px]:gap-60 items-center max-w-7xl min-[1920px]:max-w-full mx-auto">

                    {/* Khối nội dung văn bản */}
                    <div className="space-y-4 md:space-y-2 min-[1920px]:space-y-6 text-center md:text-left relative z-20" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
                        <h1 className="text-3xl xl:text-4xl 2xl:text-5xl min-[1920px]:text-8xl min-[2560px]:text-[10rem] font-normal">
                            <span className="text-[#006633]">Rovi Holdings</span>
                        </h1>
                        <h2 className="text-3xl xl:text-4xl 2xl:text-5xl min-[1920px]:text-8xl min-[2560px]:text-[10rem] font-medium text-[#2D3746] leading-tight">
                            Kết nối hành trình
                            <br />
                            chạm đến tương lai
                        </h2>
                        <p className="text-[#2D3746] text-xs xl:text-sm 2xl:text-base min-[1920px]:text-3xl min-[2560px]:text-5xl leading-relaxed max-w-sm mx-auto md:mx-0 2xl:max-w-md min-[1920px]:max-w-4xl min-[2560px]:max-w-6xl text-pretty font-normal" style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}>
                            <span className="font-semibold">
                                Rovi Holdings là công ty đầu tư, thương mại và dịch vụ đa ngành nghề.
                            </span>{" "}
                            Tập đoàn được thành lập bởi những doanh nhân đầy đam mê kinh nghiệm, tâm huyết, cùng{" "}
                            <span className="whitespace-nowrap">chí hướng</span> và khát vọng dấn thân.
                        </p>

                        <div className="flex justify-center md:justify-start pt-2">
                            <Link
                                href="/about"
                                className="group inline-flex items-center gap-2 bg-[#006633] hover:bg-red-600 text-white rounded-full font-medium transition-colors duration-300
                                text-xs px-4 py-2
                                md:text-xs md:px-4 md:py-2
                                xl:text-xs xl:px-5 xl:py-2.5
                                2xl:text-sm 2xl:px-6 2xl:py-3
                                min-[1920px]:text-3xl min-[1920px]:px-8 min-[1920px]:py-4 min-[1920px]:gap-4
                                min-[2560px]:text-6xl min-[2560px]:px-12 min-[2560px]:py-6 min-[2560px]:gap-6"
                            >
                                Về chúng tôi
                                <ChevronRight className="w-3.5 h-3.5 min-[1920px]:w-10 min-[1920px]:h-10 min-[2560px]:w-20 min-[2560px]:h-20" />
                            </Link>
                        </div>
                    </div>

                    {/* Khối hiển thị bộ đếm số liệu */}
                    <div className="flex justify-center md:justify-end items-center lg:pr-16 min-[1920px]:pr-32 min-[2560px]:pr-60 relative z-10">
                        <div className="flex items-center md:items-start relative left-0 top-0 xl:-left-8 2xl:-left-12 min-[1920px]:-left-20 xl:-top-12 2xl:-top-16 min-[1920px]:-top-32">

                            <Counter
                                value={20}
                                className="text-emerald-700 font-bold leading-[0.8] tracking-tight"
                                style={{
                                    fontSize: "clamp(100px, 18vw, 800px)",
                                    background: "linear-gradient(66.16deg, #006633 15.39%, #268C59 92.5%)",
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                    backgroundClip: "text",
                                }}
                            />

                            <div className="flex flex-col items-start justify-center ml-3 md:ml-2 xl:ml-4 2xl:ml-6 min-[1920px]:ml-12 min-[2560px]:ml-20 -mt-0 md:-mt-8 2xl:-mt-10 min-[1920px]:-mt-24 min-[2560px]:-mt-40">
                                <div
                                    className="text-[#006633] font-light leading-none -ml-0 md:-ml-3 min-[1920px]:-ml-10"
                                    style={{ fontSize: "clamp(50px, 9vw, 350px)" }}
                                >
                                    +
                                </div>
                                <div
                                    className="text-[#006633] font-extrabold leading-none mt-0"
                                    style={{ fontSize: "clamp(20px, 3.2vw, 150px)" }}
                                >
                                    năm
                                </div>
                                <div
                                    className="text-[#242424] font-medium uppercase tracking-wide leading-tight mt-1 whitespace-nowrap"
                                    style={{ fontSize: "clamp(10px, 0.6vw, 40px)" }}
                                >
                                    Chinh chiến
                                    <br />
                                    trên thị trường
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Lớp nền gradient phía dưới */}
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-white to-transparent pointer-events-none" />
        </section>
    )
}

export default RoviHoldingsHero
